import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getActiveProjectMutationKey } from '.';
import { projectService } from '../../application/ProjectService';
import { ProjectActionType } from '../../context/project/actions';
import { useProjectContext } from '../../context/project/context';
import { Project } from '../../domain/project/Project';
import useErrorMiddleware from '../errorMiddleware/useErrorMiddleware';
import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';
import { useRetrieveUser } from '../user/useRetrieveUser';

type UseRetrieveActiveProjectResponse = { set: (projectId: string) => void } & ServiceResponse<() => void, Project>;

// Hooks act as 'Adapter' layer.
export const useRetrieveActiveProject = (): UseRetrieveActiveProjectResponse => {
  const { projectId } = useParams();
  const projectIdFromUrlParams = projectId !== `:projectId` ? projectId : undefined;

  const { showSnackbarMessage } = useShowSnackbarMessage();
  const { projectState, projectDispatch } = useProjectContext();
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { data: user } = useRetrieveUser();
  const [activeProjectId, setActiveProjectId] = useState<string | undefined>(
    projectIdFromUrlParams ?? projectState.activeProject?.id ?? user?.default_project_id
  );

  const { isLoading, isError, refetch, isSuccess } = useQuery(
    [getActiveProjectMutationKey(), activeProjectId],
    () => projectService.fetch({ projectId: activeProjectId! }),
    {
      staleTime: Infinity,
      enabled: activeProjectId !== undefined,
      retry: false,
      refetchOnWindowFocus: false,
      onSuccess: (activeProject) => {
        projectDispatch({
          type: ProjectActionType.SET_ACTIVE_PROJECT,
          payload: {
            activeProject,
          },
        });
      },
      onError: (err: any) => {
        console.info(`Planner Retrieve Active Project Error: `, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }

        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  useEffect(() => {
    if (!activeProjectId && user?.default_project_id !== activeProjectId) {
      setActiveProjectId(user?.default_project_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return {
    error: isError,
    isLoading,
    success: isSuccess,
    set: setActiveProjectId,
    run: refetch,
    data: projectState.activeProject,
  };
};
